import {IFieldModel} from '@techsee/techsee-ui-common/lib/forms/_shared/simple-field-model';
import {TechseeFormBase} from '@techsee/techsee-ui-common/lib/forms/_shared/TechseeFormBase';
import {ValidationRules} from '@techsee/techsee-ui-common/lib/forms/_shared/ValidationRules';
import {ITranslate} from '../../services/LocalizationService';
import {IFieldFactory} from '../../app.contracts';
import {makeHumanReadableSet} from '@techsee/techsee-common/lib/helpers/strings.helper';

interface PasswordSettings {
    requireUppercase?: boolean;
    requireLowercase?: boolean;
    requireDigit?: boolean;
    requireSpecial?: boolean;
    minLength?: number;
}

interface PasswordOptions {
    requireDigit: boolean;
    requireUppercase: boolean;
    requireSpecial: boolean;
}

export interface ChangeSettingsFormValues {
    firstName: string;
    lastName: string;
    oldPassword: string;
    newPassword: string;
    confirmPassword: string;
}

export class ChangeSettingsForm extends TechseeFormBase {
    private readonly _translate: ITranslate;
    private readonly _createFieldModel: IFieldFactory;
    private readonly _passwordSettings: PasswordSettings | undefined;
    private readonly _allowCustom: boolean;
    private readonly _passwordOnly: boolean;

    constructor(
        translate: ITranslate,
        createFieldModel: IFieldFactory,
        passwordSettings: PasswordSettings | undefined,
        allowCustom: boolean,
        passwordOnly: boolean
    ) {
        super();

        this._translate = translate;
        this._createFieldModel = createFieldModel;
        this._passwordSettings = passwordSettings;
        this._allowCustom = allowCustom;
        this._passwordOnly = passwordOnly;

        if (!passwordOnly) {
            this.addField('firstName', this.createFirstNameField());
            this.addField('lastName', this.createLastNameField());
        }

        this.addField('oldPassword', this.createOldPasswordField());
        this.addField('newPassword', this.createNewPasswordField());
        this.addField('confirmPassword', this.createConfirmPasswordField());
    }

    getFormValues(): ChangeSettingsFormValues {
        return {
            firstName: (this.fields.firstName?.value as string) || '',
            lastName: (this.fields.lastName?.value as string) || '',
            oldPassword: (this.fields.oldPassword?.value as string) || '',
            newPassword: (this.fields.newPassword?.value as string) || '',
            confirmPassword: (this.fields.confirmPassword?.value as string) || ''
        };
    }

    private createFirstNameField(): IFieldModel {
        const field = this._createFieldModel('CHANGE_SETTINGS.VIEW.FIRST_NAME');
        field.placeholderKey = 'First Name';
        field.addRule({
            rule: ValidationRules.required(),
            message: this._translate('CHANGE_SETTINGS.VIEW.FIRST_NAME_REQUIRED') || 'First name is required'
        });
        field.addRule({
            rule: ValidationRules.maxLength(32),
            message: this._translate('CHANGE_SETTINGS.VIEW.FIRST_NAME_MAX_LENGTH') || 'First name is too long'
        });
        return field;
    }

    private createLastNameField(): IFieldModel {
        const field = this._createFieldModel('CHANGE_SETTINGS.VIEW.LAST_NAME');
        field.placeholderKey = 'Last Name';
        field.addRule({
            rule: ValidationRules.required(),
            message: this._translate('CHANGE_SETTINGS.VIEW.LAST_NAME_REQUIRED') || 'Last name is required'
        });
        field.addRule({
            rule: ValidationRules.maxLength(32),
            message: this._translate('CHANGE_SETTINGS.VIEW.LAST_NAME_MAX_LENGTH') || 'Last name is too long'
        });
        return field;
    }

    private createOldPasswordField(): IFieldModel {
        const field = this._createFieldModel('CHANGE_SETTINGS.VIEW.OLD_PASSWORD');
        field.setIconName('lock');
        return field;
    }

    private createNewPasswordField(): IFieldModel {
        const field = this._createFieldModel('CHANGE_SETTINGS.VIEW.NEW_PASSWORD');
        field.setIconName('lock');
        this.passwordValidationRules(field);
        return field;
    }

    private createConfirmPasswordField(): IFieldModel {
        const field = this._createFieldModel('CHANGE_SETTINGS.VIEW.CONFIRM_PASSWORD');
        field.setIconName('lock');
        return field;
    }

    async validateForm(): Promise<boolean> {
        const isValid = await super.validateForm();

        if (!isValid) {
            return false;
        }

        const oldPassword = this.fields.oldPassword?.value as string;
        const newPassword = this.fields.newPassword?.value as string;
        const confirmPassword = this.fields.confirmPassword?.value as string;

        // If old password is provided, new password is required
        if (oldPassword && !newPassword) {
            this.setFieldError('newPassword', this._translate('CHANGE_SETTINGS.VIEW.NEW_PASSWORD_REQUIRED'), false);
            return false;
        }

        // If new password is provided, confirm password must match
        if (newPassword && newPassword !== confirmPassword) {
            this.setFieldError('confirmPassword', this._translate('CHANGE_SETTINGS.VIEW.PASSWORD_MISMATCH'), false);
            return false;
        }

        // If new password is provided, confirm password is required
        if (newPassword && !confirmPassword) {
            this.setFieldError(
                'confirmPassword',
                this._translate('CHANGE_SETTINGS.VIEW.NEW_PASSWORD_REPEAT_REQUIRED') ||
                    'Please confirm your new password',
                false
            );
            return false;
        }

        return true;
    }

    private passwordValidationRules(field: IFieldModel) {
        if (this._allowCustom && this._passwordSettings) {
            this.createPasswordRules(field, {
                requireDigit: this._passwordSettings.requireDigit || false,
                requireUppercase: this._passwordSettings.requireUppercase || false,
                requireSpecial: this._passwordSettings.requireSpecial || false,
                requireLowercase: this._passwordSettings.requireLowercase || false
            });

            if (this._passwordSettings.minLength) {
                field.addRule({
                    rule: ValidationRules.minLength(this._passwordSettings.minLength),
                    message: this._translate('REACT.APP.AGENT_SETTINGS.PASSWORD_MINLENGTH', {
                        length: this._passwordSettings.minLength
                    })
                });
            }
        } else {
            field.addRule({
                rule: ValidationRules.password(),
                message: this._translate('REACT.APP.AGENT_SETTINGS.PASSWORD_PATTERN')
            });
        }
    }

    private createPasswordRules(field: IFieldModel, options: PasswordOptions & {requireLowercase: boolean}) {
        const regexps: RegExp[] = [];
        const messages: string[] = [];
        const t = this._translate;

        if (options.requireDigit) {
            regexps.push(/(?=.*\d)/);
            messages.push(t('REACT.APP.AGENT_SETTINGS.PASSWORD_REQUIRE_DIGIT'));
        }

        if (options.requireUppercase) {
            regexps.push(/(?=.*[A-Z])/);
            messages.push(t('REACT.APP.AGENT_SETTINGS.PASSWORD_REQUIRE_UPPERCASE'));
        }

        if (options.requireLowercase) {
            regexps.push(/(?=.*[a-z])/);
            messages.push(t('REACT.APP.AGENT_SETTINGS.PASSWORD_REQUIRE_LOWERCASE') || 'lowercase');
        }

        if (options.requireSpecial) {
            regexps.push(/(?=.*[~`!#$€@()%\^&*+=\-\[\]\\';,\/\\{\\}\u007C\\"\u003A<>\?])/);
            messages.push(t('REACT.APP.AGENT_SETTINGS.PASSWORD_REQUIRE_SPECIAL'));
        }

        if (regexps.length) {
            regexps.push(/.*$/);
            const finalRule = new RegExp(regexps.map((r) => r.source).join(''));
            field.addRule({
                rule: ValidationRules.regexp(finalRule),
                message: `${t('REACT.APP.AGENT_SETTINGS.PASSWORD_REQUIRE_PHRASE')} ${makeHumanReadableSet(
                    messages,
                    ',',
                    t('REACT.COMMON.PHRASES.AND')
                )}`
            });
        }
    }
}
